import { Component, Input } from '@angular/core';
import { Router } from '@angular/router';
import { EventsService } from '../../../../../../../../@shared/services/events-service';
import { EventClassResponse, EventResponse, EventScheduleResponse, EventTrackResponse, DayBreakResponse } from '../../../../../../../../@shared/models/response.module';
import { AQH_EVE_6, AQH_EVE_40, _AQH_EVE_41, _AQH_EVE_43, _AQH_EVE_42, _AQH_EVE_8 } from '../../../../../../../../@shared/models/input.module';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ScheduleEditor } from './components/schedule-editor/schedule-editor';
import { CopyScheduleModal } from './components/copy-schedule-modal/copy-schedule-modal';
import { DeleteModal } from '../../../../../../../../@shared/components/delete-modal/delete-modal';
import { StartLiveModal } from '../live-session-control/components/start-live-modal/start-live-modal';
import { ViewClassRegistrations } from '../manage-event-classes/components/view-class-registrations/view-class-registrations';
import { StorageService } from '../../../../../../../../@core/services/storage.service';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '../../../../../../../../@core/services/toast.service';

export type ScheduleType = 'startlist' | 'group_session' | 'individual_lesson';

export interface ScheduleTypeOption {
  value: ScheduleType;
  label: string;
  description: string;
  icon: string;
  defaultDuration: number; // in seconds
}

interface EventDay {
  date: Date;
  label: string;
  shortLabel: string;
  isToday: boolean;
}

interface DayBreak {
  day_break_id: number;
  after_class_id: number; // The class this break appears after
  duration_minutes: number;
  notes: string;
}

interface DayTimelineItem {
  type: 'class' | 'break';
  eventClass?: EventClassResponse;
  break?: DayBreak;
}

@Component({
  selector: 'app-manage-event-schedule',
  standalone: false,
  templateUrl: './manage-event-schedule.html',
  styleUrl: './manage-event-schedule.css'
})
export class ManageEventSchedule {
  @Input() event: EventResponse;
  eventClasses: EventClassResponse[] = [];
  filteredClasses: EventClassResponse[] = [];
  schedules: Map<number, EventScheduleResponse[]> = new Map();
  loading = true;

  // Day tabs
  eventDays: EventDay[] = [];
  selectedDay: Date | null = null;

  // For schedule type selection
  showTypeSelector: number | null = null; // event_class_id of the class showing selector

  // Tracks management
  tracks: EventTrackResponse[] = [];
  showTrackManager = false;
  newTrackName = '';
  newTrackDescription = '';
  editingTrack: EventTrackResponse | null = null;
  editTrackName = '';
  editTrackDescription = '';

  // Day breaks management
  dayBreaks: Map<string, DayBreak[]> = new Map(); // key: date string
  dayTimeline: DayTimelineItem[] = [];
  showBreakForm: number | null = null; // afterClassId where form is shown
  newBreakDuration = 30;
  newBreakNotes = 'Break';

  scheduleTypes: ScheduleTypeOption[] = [
    {
      value: 'startlist',
      label: 'Start List',
      description: 'Competition style - one rider at a time',
      icon: 'fa-list-ol',
      defaultDuration: 120 // 2 min
    },
    {
      value: 'group_session',
      label: 'Group Session',
      description: 'All riders ride together at the same time',
      icon: 'fa-users',
      defaultDuration: 3600 // 1 hour
    },
    {
      value: 'individual_lesson',
      label: 'Individual Lesson',
      description: 'Private lessons - one rider per time slot',
      icon: 'fa-user-clock',
      defaultDuration: 1800 // 30 min
    }
  ];

  constructor(
    private eventService: EventsService,
    private modalService: NgbModal,
    private translateService: TranslateService,
    private toastService: ToastService,
    private router: Router,
    private storageService: StorageService
  ) {}

  ngOnInit() {
    this.generateEventDays();
    this.loadEventClasses();
    this.loadTracks();
    this.loadDayBreaks();
  }

  generateEventDays() {
    if (!this.event.start_date || !this.event.end_date) {
      return;
    }

    const startDate = new Date(this.event.start_date);
    const endDate = new Date(this.event.end_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    this.eventDays = [];
    const currentDate = new Date(startDate);
    currentDate.setHours(0, 0, 0, 0);

    while (currentDate <= endDate) {
      const dayDate = new Date(currentDate);
      const isToday = dayDate.getTime() === today.getTime();

      this.eventDays.push({
        date: dayDate,
        label: this.formatDayLabel(dayDate),
        shortLabel: this.formatShortDayLabel(dayDate),
        isToday: isToday
      });

      currentDate.setDate(currentDate.getDate() + 1);
    }

    if (this.eventDays.length > 0) {
      const todayTab = this.eventDays.find(d => d.isToday);
      this.selectedDay = todayTab ? todayTab.date : this.eventDays[0].date;
    }
  }

  formatDayLabel(date: Date): string {
    const weekday = date.toLocaleDateString('en-GB', { weekday: 'long' }).toLowerCase();
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear().toString().slice(-2);
    return `${weekday} - ${day}.${month}.${year}`;
  }

  formatShortDayLabel(date: Date): string {
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    return `${day}.${month}`;
  }

  selectDay(day: EventDay) {
    this.selectedDay = day.date;
    this.filterClassesByDay();
    this.loadDayBreaks();
  }

  loadDayBreaks() {
    if (!this.selectedDay) return;

    const dateKey = this.selectedDay.toISOString().split('T')[0];

    // Check if we already have breaks for this day cached
    if (this.dayBreaks.has(dateKey)) {
      this.buildDayTimeline();
      return;
    }

    this.eventService.getDayBreaks(this.event.event_id, this.selectedDay.toISOString()).subscribe({
      next: (breaks: DayBreakResponse[]) => {
        const dayBreakList: DayBreak[] = breaks.map(b => ({
          day_break_id: b.day_break_id,
          after_class_id: b.after_class_id,
          duration_minutes: b.duration_minutes,
          notes: b.notes
        }));
        this.dayBreaks.set(dateKey, dayBreakList);
        this.buildDayTimeline();
      },
      error: (err) => {
        console.error('Error loading day breaks:', err);
        this.dayBreaks.set(dateKey, []);
        this.buildDayTimeline();
      }
    });
  }

  filterClassesByDay() {
    if (!this.eventClasses || !this.selectedDay) {
      this.filteredClasses = this.eventClasses || [];
      this.buildDayTimeline();
      return;
    }

    this.filteredClasses = this.eventClasses.filter(eventClass => {
      if (!eventClass.start_date_time) return false;
      const classDate = new Date(eventClass.start_date_time);
      return this.isSameDay(classDate, this.selectedDay!);
    });

    // Sort by start time
    this.filteredClasses.sort((a, b) => {
      const timeA = a.start_date_time ? new Date(a.start_date_time).getTime() : 0;
      const timeB = b.start_date_time ? new Date(b.start_date_time).getTime() : 0;
      return timeA - timeB;
    });

    this.buildDayTimeline();
  }

  buildDayTimeline() {
    this.dayTimeline = [];

    // If no selectedDay (event has no dates), still build timeline from all filtered classes
    const dateKey = this.selectedDay ? this.selectedDay.toISOString().split('T')[0] : '';
    const breaks = this.selectedDay ? (this.dayBreaks.get(dateKey) || []) : [];

    for (const eventClass of this.filteredClasses) {
      // Add the class
      this.dayTimeline.push({
        type: 'class',
        eventClass: eventClass
      });

      // Check if there's a break after this class
      const breakAfter = breaks.find(b => b.after_class_id === eventClass.event_class_id);
      if (breakAfter) {
        this.dayTimeline.push({
          type: 'break',
          break: breakAfter
        });
      }
    }
  }

  isSameDay(date1: Date, date2: Date): boolean {
    return date1.getFullYear() === date2.getFullYear() &&
           date1.getMonth() === date2.getMonth() &&
           date1.getDate() === date2.getDate();
  }

  getClassCountForDay(day: EventDay): number {
    if (!this.eventClasses) return 0;
    return this.eventClasses.filter(eventClass => {
      if (!eventClass.start_date_time) return false;
      const classDate = new Date(eventClass.start_date_time);
      return this.isSameDay(classDate, day.date);
    }).length;
  }

  loadEventClasses() {
    const params: AQH_EVE_6 = {
      event_id: this.event.event_id
    };
    this.eventService.getEventClasses(params).subscribe((data) => {
      this.eventClasses = data;
      this.filterClassesByDay();
      this.loadSchedulesForClasses();
    });
  }

  loadSchedulesForClasses() {
    this.schedules.clear();
    let pending = this.eventClasses.length;

    if (pending === 0) {
      this.loading = false;
      return;
    }

    for (const eventClass of this.eventClasses) {
      const params: AQH_EVE_40 = {
        event_class_id: eventClass.event_class_id
      };
      this.eventService.getSchedule(params).subscribe({
        next: (schedules) => {
          if (schedules && Array.isArray(schedules) && schedules.length > 0) {
            this.schedules.set(eventClass.event_class_id, schedules);
          } else {
            this.schedules.set(eventClass.event_class_id, []);
          }
          pending--;
          if (pending === 0) {
            this.loading = false;
          }
        },
        error: () => {
          this.schedules.set(eventClass.event_class_id, []);
          pending--;
          if (pending === 0) {
            this.loading = false;
          }
        }
      });
    }
  }

  getSchedulesForClass(eventClassId: number): EventScheduleResponse[] {
    return this.schedules.get(eventClassId) || [];
  }

  hasSchedules(eventClassId: number): boolean {
    const schedules = this.schedules.get(eventClassId);
    return !!schedules && schedules.length > 0;
  }

  toggleTypeSelector(eventClassId: number) {
    if (this.showTypeSelector === eventClassId) {
      this.showTypeSelector = null;
    } else {
      this.showTypeSelector = eventClassId;
    }
  }

  createScheduleWithType(eventClass: EventClassResponse, type: ScheduleTypeOption) {
    this.showTypeSelector = null;

    const existingSchedules = this.getSchedulesForClass(eventClass.event_class_id);
    const count = existingSchedules.length + 1;

    // Default schedule name to class name, add number suffix if multiple schedules
    let scheduleName: string = count > 1
      ? `${eventClass.class_name} ${count}`
      : eventClass.class_name;
    let scheduleType: 'startlist' | 'clinic';
    let sessionType: 'individual' | 'group';

    switch (type.value) {
      case 'startlist':
        scheduleType = 'startlist';
        sessionType = 'individual';
        break;
      case 'group_session':
        scheduleType = 'clinic';
        sessionType = 'group';
        break;
      case 'individual_lesson':
        scheduleType = 'clinic';
        sessionType = 'individual';
        break;
      default:
        scheduleType = 'startlist';
        sessionType = 'individual';
    }

    const params: _AQH_EVE_41 = {
      event_class_id: eventClass.event_class_id,
      schedule_name: scheduleName,
      schedule_type: scheduleType,
      session_type: sessionType,
      auto_generate: true,
      time_per_entry_seconds: type.defaultDuration
    };

    this.eventService.createSchedule(params).subscribe({
      next: (schedule) => {
        const current = this.schedules.get(eventClass.event_class_id) || [];
        current.push(schedule);
        this.schedules.set(eventClass.event_class_id, current);
        // Automatically open the editor
        this.openScheduleEditor(schedule, eventClass);
      },
      error: (err) => {
        console.error('Error creating schedule:', err);
      }
    });
  }

  openScheduleEditor(schedule: EventScheduleResponse, eventClass: EventClassResponse) {
    const modalRef = this.modalService.open(ScheduleEditor, {
      size: 'xl',
      centered: true,
      backdrop: 'static',
      keyboard: false
    });
    modalRef.componentInstance.schedule = schedule;
    modalRef.componentInstance.eventClass = eventClass;

    modalRef.result.then((updated) => {
      if (updated) {
        this.loadSchedulesForClasses();
      }
    }).catch(() => {});
  }

  openCopyScheduleModal(schedule: EventScheduleResponse, eventClass: EventClassResponse) {
    const modalRef = this.modalService.open(CopyScheduleModal, {
      size: 'md',
      centered: true
    });
    modalRef.componentInstance.schedule = schedule;
    modalRef.componentInstance.eventClass = eventClass;
    modalRef.componentInstance.event = this.event;

    modalRef.result.then((copied) => {
      if (copied) {
        this.loadSchedulesForClasses();
      }
    }).catch(() => {});
  }

  deleteSchedule(schedule: EventScheduleResponse, eventClass: EventClassResponse) {
    const modalRef = this.modalService.open(DeleteModal, { size: 'md', centered: true });
    modalRef.result.then((confirmed) => {
      if (confirmed) {
        const params: _AQH_EVE_43 = {
          schedule_id: schedule.schedule_id
        };
        this.eventService.deleteSchedule(params).subscribe({
          next: () => {
            const current = this.schedules.get(eventClass.event_class_id) || [];
            const filtered = current.filter(s => s.schedule_id !== schedule.schedule_id);
            this.schedules.set(eventClass.event_class_id, filtered);
          },
          error: (err) => {
            console.error('Error deleting schedule:', err);
          }
        });
      }
    }).catch(() => {});
  }

  startLiveSchedule(schedule: EventScheduleResponse, eventClass: EventClassResponse) {
    // Show confirmation modal
    const modalRef = this.modalService.open(StartLiveModal, {
      size: 'md',
      centered: true
    });
    modalRef.componentInstance.schedule = schedule;
    modalRef.componentInstance.eventClass = eventClass;

    modalRef.result.then((confirmed) => {
      if (confirmed) {
        // Start the live session
        this.eventService.startLiveSchedule({ schedule_id: schedule.schedule_id }).subscribe({
          next: (updatedSchedule) => {
            const current = this.schedules.get(eventClass.event_class_id) || [];
            const idx = current.findIndex(s => s.schedule_id === schedule.schedule_id);
            if (idx >= 0) {
              current[idx] = updatedSchedule;
              this.schedules.set(eventClass.event_class_id, [...current]);
            }
            // Navigate to live session control page
            this.router.navigate(['/company-profile/events', this.event.event_id, 'live', schedule.schedule_id]);
          },
          error: (err) => {
            console.error('Error starting live schedule:', err);
            this.toastService.error(this.translateService.instant('liveSession.startError'));
          }
        });
      }
    }).catch(() => {});
  }

  // Navigate to ongoing live session
  openLiveSession(schedule: EventScheduleResponse) {
    this.router.navigate(['/company-profile/events', this.event.event_id, 'live', schedule.schedule_id]);
  }

  getStatusBadgeClass(status: string): string {
    switch (status) {
      case 'draft': return 'badge-light-secondary';
      case 'published': return 'badge-light-info';
      case 'live': return 'badge-light-success';
      case 'completed': return 'badge-light-primary';
      default: return 'badge-light-secondary';
    }
  }

  getScheduleTypeLabel(schedule: EventScheduleResponse): string {
    if (schedule.session_type === 'group') {
      return 'Group';
    }
    if (schedule.schedule_type === 'clinic') {
      return 'Lesson';
    }
    return 'Start List';
  }

  getScheduleTypeIcon(schedule: EventScheduleResponse): string {
    if (schedule.session_type === 'group') {
      return 'fa-users';
    }
    if (schedule.schedule_type === 'clinic') {
      return 'fa-user-clock';
    }
    return 'fa-list-ol';
  }

  getEntryCount(schedule: EventScheduleResponse): number {
    return schedule.entries?.length || 0;
  }

  formatDuration(seconds: number): string {
    if (seconds >= 3600) {
      const hours = Math.floor(seconds / 3600);
      const mins = Math.floor((seconds % 3600) / 60);
      return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
    }
    return `${Math.floor(seconds / 60)} min`;
  }

  // Track management methods
  loadTracks() {
    this.eventService.getEventTracks(this.event.event_id).subscribe({
      next: (tracks) => {
        this.tracks = tracks || [];
      },
      error: () => {
        this.tracks = [];
      }
    });
  }

  toggleTrackManager() {
    this.showTrackManager = !this.showTrackManager;
    if (!this.showTrackManager) {
      this.resetTrackForm();
    }
  }

  resetTrackForm() {
    this.newTrackName = '';
    this.newTrackDescription = '';
    this.editingTrack = null;
    this.editTrackName = '';
    this.editTrackDescription = '';
  }

  addTrack() {
    if (!this.newTrackName.trim()) return;

    this.eventService.createEventTrack(
      this.event.event_id,
      this.newTrackName.trim(),
      this.newTrackDescription.trim() || undefined
    ).subscribe({
      next: (track) => {
        this.tracks.push(track);
        this.newTrackName = '';
        this.newTrackDescription = '';
      },
      error: (err) => {
        console.error('Error creating track:', err);
      }
    });
  }

  startEditTrack(track: EventTrackResponse) {
    this.editingTrack = track;
    this.editTrackName = track.track_name;
    this.editTrackDescription = track.track_description || '';
  }

  cancelEditTrack() {
    this.editingTrack = null;
    this.editTrackName = '';
    this.editTrackDescription = '';
  }

  saveTrack() {
    if (!this.editingTrack || !this.editTrackName.trim()) return;

    this.eventService.updateEventTrack(
      this.editingTrack.track_id,
      this.editTrackName.trim(),
      this.editTrackDescription.trim() || undefined
    ).subscribe({
      next: (updated) => {
        const idx = this.tracks.findIndex(t => t.track_id === updated.track_id);
        if (idx >= 0) {
          this.tracks[idx] = updated;
        }
        this.cancelEditTrack();
      },
      error: (err) => {
        console.error('Error updating track:', err);
      }
    });
  }

  deleteTrack(track: EventTrackResponse) {
    const modalRef = this.modalService.open(DeleteModal, { size: 'md', centered: true });
    modalRef.result.then((confirmed) => {
      if (confirmed) {
        this.eventService.deleteEventTrack(track.track_id).subscribe({
          next: () => {
            this.tracks = this.tracks.filter(t => t.track_id !== track.track_id);
          },
          error: (err) => {
            console.error('Error deleting track:', err);
          }
        });
      }
    }).catch(() => {});
  }

  getTrackName(trackId: number | null): string {
    if (!trackId) return '';
    const track = this.tracks.find(t => t.track_id === trackId);
    return track ? track.track_name : '';
  }

  updateScheduleTrack(schedule: EventScheduleResponse, trackId: number | null) {
    const params: _AQH_EVE_42 = {
      schedule_id: schedule.schedule_id,
      track_id: trackId
    };

    this.eventService.updateSchedule(params).subscribe({
      next: (updated) => {
        // Update the schedule in our local map
        for (const [classId, schedules] of this.schedules.entries()) {
          const idx = schedules.findIndex(s => s.schedule_id === schedule.schedule_id);
          if (idx >= 0) {
            schedules[idx] = updated;
            this.schedules.set(classId, [...schedules]);
            break;
          }
        }
      },
      error: (err) => {
        console.error('Error updating schedule track:', err);
      }
    });
  }

  hasMultipleTracks(): boolean {
    return this.tracks.length > 1;
  }

  // Day break methods
  toggleBreakForm(afterClassId: number) {
    if (this.showBreakForm === afterClassId) {
      this.showBreakForm = null;
    } else {
      this.showBreakForm = afterClassId;
      this.newBreakDuration = 30;
      this.newBreakNotes = 'Break';
    }
  }

  addDayBreak(afterClassId: number) {
    if (!this.selectedDay) return;

    const dateKey = this.selectedDay.toISOString().split('T')[0];
    const breaks = this.dayBreaks.get(dateKey) || [];

    // Check for existing break to calculate shift
    const existingBreak = breaks.find(b => b.after_class_id === afterClassId);
    const shiftMinutes = existingBreak
      ? this.newBreakDuration - existingBreak.duration_minutes
      : this.newBreakDuration;

    // Save to API
    this.eventService.createDayBreak(this.event.event_id, {
      after_class_id: afterClassId,
      duration_minutes: this.newBreakDuration,
      notes: this.newBreakNotes,
      break_date: this.selectedDay.toISOString()
    }).subscribe({
      next: (savedBreak: DayBreakResponse) => {
        // Update local cache - remove any existing break for this class
        let updatedBreaks = breaks.filter(b => b.after_class_id !== afterClassId);

        // Add the new/updated break
        const newBreak: DayBreak = {
          day_break_id: savedBreak.day_break_id,
          after_class_id: savedBreak.after_class_id,
          duration_minutes: savedBreak.duration_minutes,
          notes: savedBreak.notes
        };
        updatedBreaks.push(newBreak);

        this.dayBreaks.set(dateKey, updatedBreaks);
        this.showBreakForm = null;

        // Shift subsequent classes forward by the break duration
        if (shiftMinutes !== 0) {
          this.shiftClassTimesAfter(afterClassId, shiftMinutes);
        } else {
          this.buildDayTimeline();
        }
      },
      error: (err) => {
        console.error('Error creating day break:', err);
      }
    });
  }

  removeDayBreak(breakItem: DayBreak) {
    if (!this.selectedDay) return;

    const dateKey = this.selectedDay.toISOString().split('T')[0];

    // Delete from API
    this.eventService.deleteDayBreak(breakItem.day_break_id).subscribe({
      next: () => {
        // Update local cache
        let breaks = this.dayBreaks.get(dateKey) || [];
        breaks = breaks.filter(b => b.day_break_id !== breakItem.day_break_id);
        this.dayBreaks.set(dateKey, breaks);

        // Shift subsequent classes backward by the break duration
        this.shiftClassTimesAfter(breakItem.after_class_id, -breakItem.duration_minutes);
      },
      error: (err) => {
        console.error('Error deleting day break:', err);
      }
    });
  }

  shiftClassTimesAfter(afterClassId: number, shiftMinutes: number) {
    // Find index of the class after which we're adding/removing the break
    const classIndex = this.filteredClasses.findIndex(c => c.event_class_id === afterClassId);
    if (classIndex === -1) return;

    // Get classes that come after this one
    const classesToShift = this.filteredClasses.slice(classIndex + 1);

    if (classesToShift.length === 0) {
      this.buildDayTimeline();
      return;
    }

    const shiftMs = shiftMinutes * 60000;

    // Update each class and its schedules
    for (const eventClass of classesToShift) {
      // 1. Update class start time
      if (eventClass.start_date_time) {
        const currentStart = new Date(eventClass.start_date_time);
        const newStart = new Date(currentStart.getTime() + shiftMs);
        eventClass.start_date_time = newStart;

        const classParams: _AQH_EVE_8 = {
          event_class_id: eventClass.event_class_id,
          event_id: this.event.event_id,
          class_name: eventClass.class_name,
          class_description: eventClass.class_description || '',
          start_date_time: newStart,
          req_membership_plan_ids: eventClass.accepted_membership_plans?.map((p: any) => p.membership_plan_id) || [],
          max_num_registrations: eventClass.max_num_registrations ?? undefined,
          price: eventClass.price ?? undefined,
          price_currency: eventClass.price_currency ?? undefined
        };

        this.eventService.editEventClass(classParams).subscribe({
          error: (err) => console.error('Error updating class time:', err)
        });
      }

      // 2. Update schedules for this class
      const classSchedules = this.schedules.get(eventClass.event_class_id) || [];
      for (const schedule of classSchedules) {
        // Update schedule start time
        if (schedule.schedule_start_time) {
          const scheduleStart = new Date(schedule.schedule_start_time);
          const newScheduleStart = new Date(scheduleStart.getTime() + shiftMs);
          schedule.schedule_start_time = newScheduleStart;

          this.eventService.updateSchedule({
            schedule_id: schedule.schedule_id,
            schedule_start_time: newScheduleStart.toISOString()
          }).subscribe({
            error: (err) => console.error('Error updating schedule time:', err)
          });
        }

        // Update schedule entries
        if (schedule.entries) {
          for (const entry of schedule.entries) {
            if (entry.start_time || entry.end_time) {
              const newEntryStart = entry.start_time
                ? new Date(new Date(entry.start_time).getTime() + shiftMs).toISOString()
                : undefined;
              const newEntryEnd = entry.end_time
                ? new Date(new Date(entry.end_time).getTime() + shiftMs).toISOString()
                : undefined;

              if (entry.start_time) entry.start_time = new Date(newEntryStart!);
              if (entry.end_time) entry.end_time = new Date(newEntryEnd!);

              this.eventService.updateScheduleEntry({
                entry_id: entry.entry_id,
                start_time: newEntryStart,
                end_time: newEntryEnd
              }).subscribe({
                error: (err) => console.error('Error updating entry time:', err)
              });
            }
          }
        }

        // Update schedule groups
        if (schedule.groups) {
          for (const group of schedule.groups) {
            if (group.start_time || group.end_time) {
              const newGroupStart = group.start_time
                ? new Date(new Date(group.start_time).getTime() + shiftMs).toISOString()
                : undefined;
              const newGroupEnd = group.end_time
                ? new Date(new Date(group.end_time).getTime() + shiftMs).toISOString()
                : undefined;

              if (group.start_time) group.start_time = new Date(newGroupStart!);
              if (group.end_time) group.end_time = new Date(newGroupEnd!);

              this.eventService.updateScheduleGroup({
                schedule_group_id: group.schedule_group_id,
                start_time: newGroupStart,
                end_time: newGroupEnd
              }).subscribe({
                error: (err) => console.error('Error updating group time:', err)
              });
            }
          }
        }
      }
    }

    this.buildDayTimeline();
  }

  hasBreakAfterClass(classId: number): boolean {
    if (!this.selectedDay) return false;
    const dateKey = this.selectedDay.toISOString().split('T')[0];
    const breaks = this.dayBreaks.get(dateKey) || [];
    return breaks.some(b => b.after_class_id === classId);
  }

  formatBreakDuration(minutes: number): string {
    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return mins > 0 ? `${hours}h ${mins}m` : `${hours} hour${hours > 1 ? 's' : ''}`;
    }
    return `${minutes} min`;
  }

  getClassStartTime(eventClass: EventClassResponse): string {
    if (!eventClass.start_date_time) return '';
    const date = new Date(eventClass.start_date_time);
    return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
  }

  // Class status: 0 = pending, 1 = in_progress, 2 = completed
  getClassStatus(eventClass: EventClassResponse): 'pending' | 'in_progress' | 'completed' {
    if (eventClass.status === 2) return 'completed';
    if (eventClass.status === 1) return 'in_progress';
    return 'pending';
  }

  startClass(eventClass: EventClassResponse) {
    this.updateClassStatus(eventClass, 1, 'manageEvent.schedule.classStarted');
  }

  completeClass(eventClass: EventClassResponse) {
    this.updateClassStatus(eventClass, 2, 'manageEvent.schedule.classCompleted');
  }

  private updateClassStatus(eventClass: EventClassResponse, newStatus: number, successMessageKey: string) {
    const params: _AQH_EVE_8 = {
      event_class_id: eventClass.event_class_id,
      event_id: this.event.event_id,
      class_name: eventClass.class_name,
      class_description: eventClass.class_description || '',
      status: newStatus
    };

    this.eventService.editEventClass(params).subscribe({
      next: () => {
        eventClass.status = newStatus;
        this.toastService.success(this.translateService.instant(successMessageKey));
      },
      error: (err) => {
        console.error('Error updating class status:', err);
        this.toastService.error(this.translateService.instant('manageEvent.schedule.statusUpdateError'));
      }
    });
  }

  openResultsView(eventClass: EventClassResponse) {
    // Navigate to the results tab with the selected class
    this.router.navigate(
      ['/company-profile/manage-event', this.event.event_id],
      {
        queryParams: {
          tab: 'results',
          classId: eventClass.event_class_id
        }
      }
    );
  }
}
